import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface RemuneracionesEmpresa {
  empresa_id: number;
  rut_empresa: string;
  nombre_empresa: string;
  // rut no es único por empresa en Remuneraciones (varias empresas de
  // distintos clientes pueden compartir rut) — codigo_empresa es lo que
  // permite distinguirlas. Puede venir null en respuestas de un backend
  // que todavía no lo expone.
  codigo_empresa?: string | null;
}

interface CachedCompanyData {
  companyId: number;
  periodoInicio: string;
  periodoTermino: string;
  lastUpdated: string | null;
  fetchedAt: number;
  data: any;
}

// Remuneraciones es un proyecto externo, pero el navegador nunca habla con él:
// todas las llamadas pasan por el backend de Confinanzas
// (api_confinanzas/remuneraciones), que guarda el token de Remuneraciones en
// el servidor, resuelve el rut desde la empresa activa de la sesión y valida
// que cada company_id pedido pertenezca a ese rut. Por eso acá no hay token,
// ni rut como parámetro, ni CORS que resolver.
@Injectable({
  providedIn: 'root'
})
export class RemuneracionesExternalService {

  private baseUrl = environment.getBackendUrl('remuneraciones');

  // Respaldo por si /last_updated/ no responde o no cambia cuando debería:
  // el cache nunca queda pegado más de este tiempo.
  private readonly CACHE_TTL_MS = 30 * 60 * 1000;
  private cache: CachedCompanyData | null = null;

  constructor(private http: HttpClient) { }

  // La empresa activa se identifica igual que en CompanyProfileService (uuid
  // de localStorage). El backend lo valida contra los accesos del usuario.
  private companyUuid(): string {
    try {
      return JSON.parse(localStorage.getItem('currentUser'))?.company_uuid || '';
    } catch {
      return '';
    }
  }

  // Solo las empresas de Remuneraciones cuyo rut calza con el de la empresa
  // activa (el backend ya filtra por rut; ver RemuneracionesEmpresa).
  getCompanies(): Observable<RemuneracionesEmpresa[]> {
    return this.http.get<{ empresas: RemuneracionesEmpresa[] }>(`${this.baseUrl}/companies/`, {
      params: { company_uuid: this.companyUuid() },
    }).pipe(
      map(resp => resp?.empresas || []),
    );
  }

  getCompanyData(companyId: number, periodoInicio: string, periodoTermino: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/data/`, {
      params: {
        company_uuid: this.companyUuid(),
        company_id: companyId.toString(),
        periodo_inicio: periodoInicio,
        periodo_termino: periodoTermino,
      },
    });
  }

  getLastUpdated(companyId: number): Observable<string | null> {
    return this.http.get<{ last_updated: string | null }>(`${this.baseUrl}/last_updated/`, {
      params: { company_uuid: this.companyUuid(), company_id: companyId.toString() },
    }).pipe(
      map(resp => resp?.last_updated ?? null),
    );
  }

  // last_updated puede venir null legítimamente (Remuneraciones aún no le
  // calcula timestamp a esa empresa) — eso NO es lo mismo que la consulta
  // fallando. Solo un fallo real de red/HTTP debe invalidar la confianza en
  // el cache; un null repetido se trata como "sin cambios".
  private fetchLastUpdated(companyId: number): Observable<{ ok: boolean; value: string | null }> {
    return this.getLastUpdated(companyId).pipe(
      map(value => ({ ok: true, value })),
      catchError(() => of({ ok: false, value: null })),
    );
  }

  // Reusa los datos en memoria mientras Remuneraciones no reporte un
  // last_updated distinto al que teníamos guardado (y mientras no se pase el
  // TTL de respaldo). Si /last_updated/ falla de verdad, se refresca directo
  // sin cache.
  getCompanyDataCached(companyId: number, periodoInicio: string, periodoTermino: string): Observable<any> {
    const cached = this.cache;
    const mismaConsulta = !!cached
      && cached.companyId === companyId
      && cached.periodoInicio === periodoInicio
      && cached.periodoTermino === periodoTermino;

    return this.fetchLastUpdated(companyId).pipe(
      switchMap(({ ok, value: lastUpdated }) => {
        const cacheVigente = mismaConsulta
          && ok
          && (Date.now() - cached.fetchedAt) < this.CACHE_TTL_MS
          && lastUpdated === cached.lastUpdated;

        if (cacheVigente) {
          return of(cached.data);
        }

        return this.getCompanyData(companyId, periodoInicio, periodoTermino).pipe(
          map(data => {
            this.cache = ok
              ? { companyId, periodoInicio, periodoTermino, lastUpdated, fetchedAt: Date.now(), data }
              : null;
            return data;
          }),
        );
      }),
    );
  }

  // Ventana de períodos que usa Panel Resumen: año actual y los 2 anteriores.
  private defaultPeriodo(): { periodoInicio: string; periodoTermino: string } {
    const anioActual = new Date().getFullYear();
    return { periodoInicio: `${anioActual - 2}-1`, periodoTermino: `${anioActual}-12` };
  }

  // Precarga best-effort: se dispara al iniciar sesión y al cambiar de
  // empresa para que Panel Resumen encuentre el cache ya tibio. No informa
  // errores al usuario — si falla, Panel Resumen igual hace su propia
  // consulta al entrar.
  preload(): void {
    this.getCompanies().pipe(
      catchError(() => of([])),
      switchMap(empresas => {
        // Si el rut es ambiguo (más de una empresa en Remuneraciones),
        // no hay una correcta para precargar sin que el usuario elija en
        // Panel Resumen — mejor no calentar el cache de la equivocada.
        if (empresas.length !== 1) {
          return of(null);
        }
        const empresa = empresas[0];
        const { periodoInicio, periodoTermino } = this.defaultPeriodo();
        return this.getCompanyDataCached(empresa.empresa_id, periodoInicio, periodoTermino).pipe(
          catchError(() => of(null)),
        );
      }),
    ).subscribe();
  }
}
